export const SEO_PAGES = [
  {
    slug: 'link-in-bio-tool',
    group: 'Core',
    eyebrow: 'Link in Bio Tool',
    title: 'A link in bio tool for creators who need more than a list of links',
    description: 'Build a public profile with links, products, tips, subscribers, analytics, QR codes, and optional private vault content.',
    intent: 'For creators comparing link in bio tools and deciding where to build their main profile.',
    primaryCta: 'Start Building',
    secondaryCta: 'See Discover',
    keywords: ['link in bio tool', 'bio link page', 'creator profile tool'],
    sections: [
      {
        title: 'What Linkit gives you',
        body: 'Linkit combines a public creator profile, link hub, shop cards, tip jar, subscriber tools, QR export, and analytics in one place. It is built for creators who want a profile that feels like their own digital front door.',
      },
      {
        title: 'Why it is different',
        body: 'Most bio link pages stop at buttons. Linkit adds audience tools, real profile styling, gated links, public discovery, and supported private vault workflows so the same profile can grow from a simple link page into a creator operating surface.',
      },
      {
        title: 'Best for',
        body: 'Independent creators, artists, developers, educators, consultants, and small sellers who want one clean profile for links, products, subscriber capture, and audience navigation.',
      },
    ],
    faqs: [
      ['Can I use Linkit as my Instagram bio link?', 'Yes. Put your Linkit profile URL in your Instagram, YouTube, X, LinkedIn, newsletter, or email signature.'],
      ['Does Linkit support products?', 'Yes. You can add shop cards and external buy links for products, services, and digital offers.'],
      ['Does Linkit include QR codes?', 'Yes. QR Studio lets you export a profile QR code for cards, posters, packaging, and events.'],
    ],
  },
  {
    slug: 'link-in-bio-with-shop',
    group: 'Commerce',
    eyebrow: 'Creator Storefront',
    title: 'A link in bio with shop cards for creators selling products or services',
    description: 'Create one profile for links, product cards, buy buttons, tips, gated content, and subscriber capture.',
    intent: 'For creators who want a bio link page that can also act as a lightweight storefront.',
    primaryCta: 'Build Your Storefront',
    secondaryCta: 'See Commerce Features',
    secondaryHref: '/#commerce',
    keywords: ['link in bio with shop', 'creator storefront', 'bio link shop'],
    sections: [
      {
        title: 'Sell without hiding the rest of your work',
        body: 'Linkit lets you place product cards beside your links, social destinations, tip jar, and subscriber tools. Visitors can discover what you make and act without hunting through multiple pages.',
      },
      {
        title: 'Use your existing checkout',
        body: 'Shop cards can point to your external store, Stripe payment links, Gumroad, Shopify, PayPal, UPI, or any supported destination. Linkit organizes the path; your checkout remains where you want it.',
      },
      {
        title: 'Track what gets attention',
        body: 'Creator analytics help you understand profile views and link clicks so you can see which offers and links deserve more focus.',
      },
    ],
    faqs: [
      ['Is Linkit a full ecommerce platform?', 'No. Linkit is a profile and storefront layer that points visitors to your existing purchase links.'],
      ['Can I sell digital products?', 'Yes. You can link to digital product checkout pages, downloads, courses, templates, or memberships.'],
      ['Can I mix normal links and products?', 'Yes. The profile can include both content links and product cards.'],
    ],
  },
  {
    slug: 'link-in-bio-with-analytics',
    group: 'Analytics',
    eyebrow: 'Audience Analytics',
    title: 'A link in bio with analytics for creators who want to know what works',
    description: 'Publish a Linkit profile and understand profile views, link clicks, product interest, and audience activity.',
    intent: 'For creators who need more signal than a simple profile link list.',
    primaryCta: 'Start Tracking',
    secondaryCta: 'See Features',
    secondaryHref: '/#features',
    keywords: ['link in bio with analytics', 'bio link analytics', 'creator analytics'],
    sections: [
      {
        title: 'Measure the page people actually visit',
        body: 'Your bio link page is often the first place a follower becomes a subscriber, buyer, or reader. Linkit helps you see what visitors click after they arrive.',
      },
      {
        title: 'Improve the order of your links',
        body: 'Analytics make it easier to move high-performing links up, remove distractions, and test different calls to action without guessing.',
      },
      {
        title: 'Connect profile design with outcomes',
        body: 'A beautiful profile is useful only if it helps visitors act. Linkit keeps visual customization close to audience and click data.',
      },
    ],
    faqs: [
      ['What can analytics help with?', 'They help you understand which links, offers, and profile sections get attention.'],
      ['Do I need analytics as a small creator?', 'Yes. Even simple click data can show what your audience cares about.'],
      ['Can analytics work with shop links?', 'Yes. Product and external links can be tracked as profile actions.'],
    ],
  },
  {
    slug: 'bio-link-for-creators',
    group: 'Niche',
    eyebrow: 'For Creators',
    title: 'A bio link page for creators building an audience, shop, and public identity',
    description: 'Linkit helps creators organize links, sell products, collect subscribers, share QR codes, and build a premium public profile.',
    intent: 'For creators searching for a general bio link page built around creator workflows.',
    primaryCta: 'Create Your Profile',
    secondaryCta: 'Discover Creators',
    secondaryHref: '/discover',
    keywords: ['bio link for creators', 'creator bio page', 'creator link in bio'],
    sections: [
      {
        title: 'One place for your public work',
        body: 'Put your best links, latest products, social channels, booking pages, tip jar, and subscriber call to action behind one clean profile.',
      },
      {
        title: 'Built for changing priorities',
        body: 'Creators change what they promote every week. Linkit is designed so you can update campaigns, reorder links, and highlight the next important action quickly.',
      },
      {
        title: 'A profile that can grow with you',
        body: 'Start with a few links. Add shop cards, gated content, analytics, QR codes, and private vault features when your creator business needs them.',
      },
    ],
    faqs: [
      ['What kind of creators can use Linkit?', 'Artists, developers, writers, musicians, coaches, educators, sellers, and small creative businesses.'],
      ['Can I use my profile as a portfolio?', 'Yes. Linkit works as a compact portfolio and action page.'],
      ['Can I collect subscribers?', 'Yes. Linkit includes subscriber tools for creator updates.'],
    ],
  },
  {
    slug: 'link-in-bio-for-instagram',
    group: 'Niche',
    eyebrow: 'Instagram Bio Link',
    title: 'A link in bio for Instagram that can hold more than your latest post',
    description: 'Use Linkit as your Instagram bio link for content, products, subscriber capture, tip jar links, and QR codes.',
    intent: 'For Instagram creators who need one link that sends followers to the right destination.',
    primaryCta: 'Claim Your Instagram Bio Link',
    secondaryCta: 'Discover Creators',
    secondaryHref: '/discover',
    keywords: ['link in bio for Instagram', 'Instagram bio link tool', 'Instagram link page'],
    sections: [
      {
        title: 'Turn profile visits into action',
        body: 'Instagram gives you limited bio space. Linkit turns that one link into a focused profile where followers can watch, buy, subscribe, book, tip, or read.',
      },
      {
        title: 'Match campaigns to the moment',
        body: 'Promote a launch, a product, a newsletter, a new video, or an event from the same profile without constantly replacing your whole online presence.',
      },
      {
        title: 'Use QR codes offline',
        body: 'QR Studio helps you send people from packaging, events, flyers, and business cards to the same profile you use in your Instagram bio.',
      },
    ],
    faqs: [
      ['Can I put a Linkit URL in my Instagram bio?', 'Yes. Your Linkit public profile URL is made for social bios.'],
      ['Can I promote products from Instagram?', 'Yes. Add product cards or checkout links to your Linkit page.'],
      ['Can I update the page after posting?', 'Yes. You can update Linkit without changing old Instagram posts.'],
    ],
  },
  {
    slug: 'free-link-in-bio',
    group: 'Core',
    eyebrow: 'Link in Bio',
    title: 'A link in bio page that can grow with your creator work',
    description: 'Build a Linkit profile for social links, products, subscriber capture, QR sharing, analytics, and profile SEO.',
    intent: 'For creators searching for a link in bio page before choosing a long-term profile tool.',
    primaryCta: 'Create Profile',
    secondaryCta: 'See Features',
    secondaryHref: '/#features',
    keywords: ['link in bio', 'bio link page', 'link in bio tool'],
    sections: [
      {
        title: 'Start simple',
        body: 'Create one public profile for your social links, latest work, products, booking pages, tip links, and subscriber call to action.',
      },
      {
        title: 'Keep the page useful as you grow',
        body: 'When your audience grows, Linkit can also support analytics, shop cards, QR sharing, gated links, and deeper profile styling.',
      },
      {
        title: 'Use one URL everywhere',
        body: 'Put the same profile in social bios, newsletters, email signatures, event materials, product packaging, and QR cards.',
      },
    ],
    faqs: [
      ['Can I create a Linkit profile?', 'Linkit supports public creator profiles and advanced workflows as your setup grows. Availability may depend on rollout or invite access.'],
      ['Can I use it across multiple social platforms?', 'Yes. Use the same Linkit URL on Instagram, YouTube, LinkedIn, X, newsletters, and offline QR materials.'],
      ['Can the page promote my products too?', 'Yes. Add product cards, checkout links, digital products, services, tips, or booking destinations.'],
    ],
  },
  {
    slug: 'link-in-bio-for-artists',
    group: 'Niche',
    eyebrow: 'Artists',
    title: 'A link in bio for artists sharing work, commissions, shops, and updates',
    description: 'Create one artist profile for portfolio links, shop products, commission forms, social channels, tips, and subscriber updates.',
    intent: 'For artists who need a simple public hub for their creative work and audience.',
    primaryCta: 'Build Artist Profile',
    secondaryCta: 'Browse Discover',
    keywords: ['link in bio for artists', 'artist bio link', 'artist profile page'],
    sections: [
      {
        title: 'Show where your work lives',
        body: 'Link to your portfolio, prints, shop, commissions, videos, exhibitions, newsletter, and social profiles from one memorable page.',
      },
      {
        title: 'Make support easy',
        body: 'Add tip jar links, product cards, or commission forms so followers can support your work without asking where to go.',
      },
      {
        title: 'Keep the page visual',
        body: 'A creator profile should match the work behind it. Linkit gives artists enough control to make the page feel intentional instead of generic.',
      },
    ],
    faqs: [
      ['Can I use Linkit for commissions?', 'Yes. Link to commission forms, booking pages, or direct contact flows.'],
      ['Can I add shop items?', 'Yes. Use shop cards for prints, originals, digital goods, or merch.'],
      ['Can I share it at events?', 'Yes. Export a QR code and place it on table cards, tags, flyers, or packaging.'],
    ],
  },
  {
    slug: 'link-in-bio-for-developers',
    group: 'Niche',
    eyebrow: 'Developers',
    title: 'A link in bio for developers, indie hackers, and technical creators',
    description: 'Use Linkit to share projects, GitHub, products, API docs, newsletters, demos, and personal brand links from one profile.',
    intent: 'For developers who want a cleaner public profile than a scattered list of links.',
    primaryCta: 'Create Developer Profile',
    secondaryCta: 'View Blueprint',
    secondaryHref: '/blueprint',
    keywords: ['link in bio for developers', 'developer profile page', 'indie hacker link page'],
    sections: [
      {
        title: 'Put your projects in one place',
        body: 'Share your GitHub, portfolio, SaaS product, documentation, newsletter, demos, social channels, and contact links from one public profile.',
      },
      {
        title: 'Useful for launches',
        body: 'When you launch a project, your profile can route people to the product, waitlist, changelog, docs, roadmap, or support page without scattering attention.',
      },
      {
        title: 'Developer-friendly identity',
        body: 'Linkit includes API-key based developer endpoints, analytics, and profile tooling that fit technical users who want more control.',
      },
    ],
    faqs: [
      ['Can I link to GitHub?', 'Yes. Add GitHub, docs, demos, products, or any public project page.'],
      ['Can I use it for a SaaS launch?', 'Yes. Use one profile for waitlist, product links, docs, updates, and founder contact.'],
      ['Can I create multiple profiles?', 'Yes. Linkit supports multiple profiles under one account.'],
    ],
  },
  {
    slug: 'linktree-alternative',
    group: 'Comparison',
    eyebrow: 'Alternative',
    title: 'A Linktree alternative for creators who want a richer public profile',
    description: 'Compare Linkit as a Linktree alternative for creators who need links, shop cards, subscribers, analytics, QR codes, and private vault support.',
    intent: 'For users comparing Linkit against established link in bio tools.',
    primaryCta: 'Try Linkit',
    secondaryCta: 'Compare Features',
    secondaryHref: '/#features',
    keywords: ['Linktree alternative', 'alternative to Linktree', 'best Linktree alternative'],
    sections: [
      {
        title: 'Why creators look for an alternative',
        body: 'A simple list of links is useful at the beginning. As a creator grows, the page often needs product cards, subscriber capture, profile styling, analytics, QR codes, and better ways to guide different audience segments.',
      },
      {
        title: 'Where Linkit fits',
        body: 'Linkit is built as a creator profile and audience surface, not only a link list. It brings public profile design, shop links, gated links, QR export, analytics, and supported private vault features together.',
      },
      {
        title: 'When to choose Linkit',
        body: 'Choose Linkit if you want a profile that can grow into your creator storefront, subscriber hub, and public identity instead of staying only as a basic button page.',
      },
    ],
    faqs: [
      ['Is Linkit the same as Linktree?', 'No. Both can host links, but Linkit focuses on richer creator profiles, shop workflows, analytics, QR export, and private vault support.'],
      ['Can I move from Linktree?', 'Yes. You can recreate your main links and then add richer sections over time.'],
      ['Should I use comparison pages before switching?', 'Yes. Compare the exact workflows you need: products, subscribers, analytics, QR codes, and profile design.'],
    ],
  },
  {
    slug: 'beacons-alternative',
    group: 'Comparison',
    eyebrow: 'Alternative',
    title: 'A Beacons alternative for creators who want a focused profile and storefront',
    description: 'Linkit gives creators a polished profile for links, products, tips, subscribers, analytics, QR codes, and supported private vault content.',
    intent: 'For creators comparing alternatives to Beacons and other creator hub tools.',
    primaryCta: 'Create a Linkit Profile',
    secondaryCta: 'Explore Discover',
    keywords: ['Beacons alternative', 'creator hub alternative', 'bio link storefront'],
    sections: [
      {
        title: 'A focused public profile',
        body: 'Linkit keeps the public page direct: profile, links, shop cards, subscriber tools, tip jar, and action-focused sections that visitors can understand quickly.',
      },
      {
        title: 'Useful for early creators',
        body: 'Even before you have a large audience, Linkit helps you present your work clearly and start collecting the first signals: clicks, subscribers, and product interest.',
      },
      {
        title: 'Built around ownership',
        body: 'Linkit separates public creator content from supported private vault workflows, giving creators a clearer boundary between what is promotional and what is private.',
      },
    ],
    faqs: [
      ['Is Linkit only for influencers?', 'No. It also fits artists, developers, consultants, educators, and small creator businesses.'],
      ['Can I use Linkit as a lightweight storefront?', 'Yes. Product cards can route visitors to your preferred checkout.'],
      ['Can I use Linkit with a small audience?', 'Yes. It is useful as a first public home before your audience is large.'],
    ],
  },
  {
    slug: 'how-to-create-link-in-bio-page',
    group: 'Guide',
    eyebrow: 'Guide',
    title: 'How to create a link in bio page that actually sends visitors somewhere useful',
    description: 'A practical guide to building a better link in bio page with clear sections, strong calls to action, products, subscribers, and analytics.',
    intent: 'For creators researching how to build a better bio link page before choosing a tool.',
    primaryCta: 'Build It in Linkit',
    secondaryCta: 'Discover Creators',
    secondaryHref: '/discover',
    keywords: ['how to create a link in bio page', 'make a bio link page', 'bio link page guide'],
    sections: [
      {
        title: 'Start with one primary action',
        body: 'Decide what you want visitors to do first: buy, subscribe, watch, book, read, or contact. A good bio link page is not every link you own; it is the shortest path to the next useful action.',
      },
      {
        title: 'Group links by visitor intent',
        body: 'Separate products, content, social channels, bookings, and subscriber actions. Visitors should not need to guess which button matters.',
      },
      {
        title: 'Measure and adjust',
        body: 'After publishing, use click and profile data to move stronger links up and remove links that distract from your main goal.',
      },
    ],
    faqs: [
      ['How many links should I add?', 'Start with five to seven important actions. Add more only when they help a clear visitor goal.'],
      ['Should my profile include products?', 'Yes, if selling is part of your creator workflow. Put products where visitors can find them quickly.'],
      ['Should I use a QR code?', 'Yes, if you meet people offline or want your profile on printed material.'],
    ],
  },
  {
    slug: 'artist-link-in-bio-template',
    group: 'Template',
    eyebrow: 'Artist Profile Template',
    title: 'Artist link in bio template for portfolios, commissions, and shop links',
    description: 'Start with a Linkit artist profile structure for portfolio links, commissions, prints, social channels, tips, QR codes, and subscriber updates.',
    intent: 'For artists who want a ready profile structure instead of starting from a blank bio link page.',
    primaryCta: 'Use Artist Template',
    secondaryCta: 'Browse Artists',
    secondaryHref: '/discover',
    keywords: ['artist link in bio template', 'artist bio page template', 'artist portfolio link'],
    sections: [
      {
        title: 'Lead with the current body of work',
        body: 'An artist template should put the strongest current work first, then route visitors to portfolio pages, shop links, commission forms, social profiles, and subscriber updates.',
      },
      {
        title: 'Make support visible',
        body: 'Print drops, commission requests, tip links, and booking forms should be easy to find without forcing visitors through every social profile first.',
      },
      {
        title: 'Use one QR across offline touchpoints',
        body: 'The same Linkit profile can sit behind event table cards, packaging, exhibition labels, and social bios while the artist updates the page for each campaign.',
      },
    ],
    faqs: [
      ['What should an artist put in a link in bio?', 'Start with portfolio, shop, commissions, current work, newsletter, and social links.'],
      ['Can artists use QR codes with this template?', 'Yes. QR Studio can point offline visitors to the same profile used in social bios.'],
      ['Can this work for photographers and makers?', 'Yes. The template fits illustrators, photographers, craft sellers, performers, and visual creators.'],
    ],
  },
  {
    slug: 'developer-link-in-bio-template',
    group: 'Template',
    eyebrow: 'Developer Profile Template',
    title: 'Developer link in bio template for projects, GitHub, docs, and launches',
    description: 'Create a Linkit developer profile for project links, GitHub, demos, API docs, product updates, newsletters, and launch traffic.',
    intent: 'For developers and indie hackers who want one public profile for technical projects and products.',
    primaryCta: 'Use Developer Template',
    secondaryCta: 'View Blueprint',
    secondaryHref: '/blueprint',
    keywords: ['developer link in bio template', 'developer profile template', 'indie hacker bio link'],
    sections: [
      {
        title: 'Put the active project first',
        body: 'A developer profile should lead with the live product, demo, waitlist, or open source project that matters most right now.',
      },
      {
        title: 'Separate product paths from proof paths',
        body: 'Visitors may want the app, docs, GitHub, changelog, newsletter, roadmap, or contact link. A clean template keeps those paths readable.',
      },
      {
        title: 'Useful before and after launch',
        body: 'Before launch, the page can collect waitlist attention. After launch, it can route users to docs, support, pricing, updates, and demos.',
      },
    ],
    faqs: [
      ['Can I link GitHub and docs?', 'Yes. Add GitHub, API docs, demos, products, changelogs, and newsletters.'],
      ['Is this only for indie hackers?', 'No. It also works for engineers, founders, technical writers, and developer advocates.'],
      ['Can I use it for launch traffic?', 'Yes. Use one profile to route launch attention to the right product destination.'],
    ],
  },
  {
    slug: 'musician-link-in-bio-template',
    group: 'Template',
    eyebrow: 'Musician Profile Template',
    title: 'Musician link in bio template for releases, shows, merch, and streaming links',
    description: 'Build a Linkit music profile for new releases, streaming platforms, tour dates, merch, videos, fan updates, and QR sharing.',
    intent: 'For musicians who need one profile for songs, shows, merchandise, and fan actions.',
    primaryCta: 'Use Musician Template',
    secondaryCta: 'Discover Creators',
    secondaryHref: '/discover',
    keywords: ['musician link in bio template', 'music bio link page', 'artist music link page'],
    sections: [
      {
        title: 'Promote the latest release first',
        body: 'A musician profile should put the current song, album, video, or ticket link first so fans do not need to search across platforms.',
      },
      {
        title: 'Keep streaming and selling together',
        body: 'Streaming links, merch, tour dates, videos, newsletter signup, and social profiles can sit on one page without losing the main release action.',
      },
      {
        title: 'Bridge posters and social bios',
        body: 'A Linkit QR code can send people from flyers, venues, merch tables, and packaging to the same profile used in social bios.',
      },
    ],
    faqs: [
      ['Can musicians add streaming links?', 'Yes. Add Spotify, Apple Music, YouTube, Bandcamp, SoundCloud, or any release page.'],
      ['Can I promote merch and shows?', 'Yes. Add merch, ticket links, tour dates, videos, and fan signup actions.'],
      ['Can the same page work for each release?', 'Yes. Keep the URL stable and update the first links around each release.'],
    ],
  },
  {
    slug: 'shop-link-in-bio-template',
    group: 'Template',
    eyebrow: 'Shop Page Template',
    title: 'Shop link in bio template for products, offers, and checkout links',
    description: 'Use a Linkit shop profile to organize featured products, checkout links, offers, tips, subscriber capture, and campaign links.',
    intent: 'For small sellers and creators who want a simple storefront-style bio link page.',
    primaryCta: 'Use Shop Template',
    secondaryCta: 'View Storefront Page',
    secondaryHref: '/link-in-bio-with-shop',
    keywords: ['shop link in bio template', 'bio link shop template', 'creator storefront template'],
    sections: [
      {
        title: 'Feature the best offer first',
        body: 'A shop template should make the main product or current campaign easy to act on before showing secondary products or content links.',
      },
      {
        title: 'Use your existing checkout',
        body: 'Linkit can route visitors to Stripe, Shopify, Gumroad, PayPal, UPI, booking pages, or any preferred checkout destination.',
      },
      {
        title: 'Measure product attention',
        body: 'Click data helps sellers see which products, offers, or categories deserve better placement on the profile.',
      },
    ],
    faqs: [
      ['Is Linkit a full store builder?', 'No. It is a profile and routing layer for products and checkout links.'],
      ['Can I add digital products?', 'Yes. Add templates, courses, downloads, memberships, services, or physical products.'],
      ['Can the shop template include normal links?', 'Yes. Product links and normal creator links can live on the same profile.'],
    ],
  },
  {
    slug: 'coach-link-in-bio-template',
    group: 'Template',
    eyebrow: 'Coach Profile Template',
    title: 'Coach link in bio template for bookings, programs, content, and leads',
    description: 'Create a Linkit coach profile for booking links, programs, testimonials, lead magnets, newsletters, social proof, and subscriber capture.',
    intent: 'For coaches and consultants who need one bio link page for leads, bookings, and content.',
    primaryCta: 'Use Coach Template',
    secondaryCta: 'Create Profile',
    secondaryHref: '/login?signup=true',
    keywords: ['coach link in bio template', 'consultant bio link page', 'booking link in bio'],
    sections: [
      {
        title: 'Start with the next conversion',
        body: 'A coach profile should lead with the next visitor action: book a call, join a program, download a resource, or subscribe for updates.',
      },
      {
        title: 'Support trust quickly',
        body: 'The page can organize proof links, content, testimonials, program pages, social channels, and contact options around one clear action.',
      },
      {
        title: 'Keep campaigns flexible',
        body: 'Coaches can rotate the first link for launches, webinars, consultation windows, lead magnets, and newsletter growth.',
      },
    ],
    faqs: [
      ['Can I add booking links?', 'Yes. Add Calendly, booking pages, forms, program links, newsletters, and content.'],
      ['Can consultants use this too?', 'Yes. The same structure works for coaches, consultants, educators, and service providers.'],
      ['What should be first?', 'Put the highest-priority conversion first, usually booking, program signup, or a lead magnet.'],
    ],
  },
  {
    slug: 'portfolio-link-in-bio-template',
    group: 'Template',
    eyebrow: 'Portfolio Profile Template',
    title: 'Portfolio link in bio template for work, projects, contact, and proof',
    description: 'Build a Linkit portfolio profile for selected work, project links, resume pages, case links, contact, social profiles, and QR sharing.',
    intent: 'For creators and professionals who want a compact portfolio link for social bios and offline sharing.',
    primaryCta: 'Use Portfolio Template',
    secondaryCta: 'Discover Creators',
    secondaryHref: '/discover',
    keywords: ['portfolio link in bio template', 'portfolio bio link', 'creative portfolio link'],
    sections: [
      {
        title: 'Show selected work, not every link',
        body: 'A portfolio template should guide visitors to the best work first, then provide supporting links like resume, contact, writing, projects, and socials.',
      },
      {
        title: 'Make contact obvious',
        body: 'Recruiters, clients, and collaborators should be able to find contact or booking links without scrolling through unrelated destinations.',
      },
      {
        title: 'Useful for digital and offline sharing',
        body: 'The same portfolio profile can be used in social bios, resumes, email signatures, business cards, event badges, and QR posters.',
      },
    ],
    faqs: [
      ['Can Linkit work as a mini portfolio?', 'Yes. Use it as a compact profile that points to selected work and contact links.'],
      ['Can I add resume or project links?', 'Yes. Add resumes, portfolios, project pages, social profiles, writing, and booking links.'],
      ['Is this only for designers?', 'No. It fits developers, writers, photographers, consultants, students, and creative professionals.'],
    ],
  },
  {
    slug: 'best-link-in-bio-tools-for-creators',
    group: 'Guide',
    eyebrow: 'Guide',
    title: 'What to look for in the best link in bio tools for creators',
    description: 'A practical checklist for choosing a link in bio tool: profile design, products, analytics, subscribers, QR codes, speed, and trust.',
    intent: 'For users comparing link in bio tools before signing up.',
    primaryCta: 'Compare Linkit',
    secondaryCta: 'Start Building',
    secondaryHref: '/login?signup=true',
    keywords: ['best link in bio tools', 'best bio link tools for creators', 'creator link tools'],
    sections: [
      {
        title: 'Do not compare only button lists',
        body: 'Most tools can show a list of links. The better question is whether the tool helps you sell, collect subscribers, track clicks, update campaigns, and make a profile that feels credible.',
      },
      {
        title: 'Check the creator workflows',
        body: 'Look for shop cards, subscriber capture, analytics, QR export, social embeds, gated links, profile SEO, and enough style control to make the page yours.',
      },
      {
        title: 'Pick a tool you can grow into',
        body: 'Switching your bio link is easy; rebuilding a public identity is slower. Choose a platform that can handle your next stage, not only your first few links.',
      },
    ],
    faqs: [
      ['What is the most important feature?', 'A clear profile that makes visitors act. Everything else should support that.'],
      ['Do small creators need a paid-looking page?', 'Yes. A credible profile helps even before you have large social proof.'],
      ['Why compare analytics?', 'Because analytics show whether your profile layout and calls to action are working.'],
    ],
  },
];

export const SEO_PAGE_MAP = Object.fromEntries(SEO_PAGES.map((page) => [page.slug, page]));
export const SEO_PAGE_SLUGS = SEO_PAGES.map((page) => page.slug);
